import { useShow, useShowEpisodes, useMarkedEpisodes } from '../hooks/useShows'
import { EpisodeList } from '../components/EpisodeList'
import { AuthGuard } from '../components/AuthGuard'
import { PageHeader } from '../components/PageHeader'
import { LoadingState } from '../components/LoadingState'
import { ErrorState } from '../components/ErrorState'
import { SwipeableEpisode } from '../components/SwipeableEpisode'

interface ShowDetailsProps {
  id: string
}

export function ShowDetails({ id }: ShowDetailsProps) {
  const showId = parseInt(id) // ID is guaranteed to be valid numeric string by route
  const { data: show, isLoading: showLoading } = useShow(showId)
  const { data: episodes, isLoading: episodesLoading } = useShowEpisodes(showId)
  const { data: markedEpisodes, isLoading: markedLoading } = useMarkedEpisodes(showId)

  if (showLoading || episodesLoading || markedLoading) {
    return <LoadingState />
  }

  if (!show) {
    return <ErrorState message="Show not found" />
  }

  // Find next unwatched episode for the episode list
  const watchedEpisodeIds = new Set(
    markedEpisodes?.filter(me => me.type === 0).map(me => me.episode_id) || [],
  )

  const nextUnwatched = episodes?.find(ep => !watchedEpisodeIds.has(ep.id))

  const stripHtml = (html: string | null) => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '')
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-slate-900">
        <PageHeader title={show.name} backTo="/shows" />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Swipeable Episode Block */}
          {episodes && markedEpisodes && episodes.length > 0 && (
            <SwipeableEpisode
              episodes={episodes}
              markedEpisodes={markedEpisodes}
              showId={show.id}
              initialEpisodeId={nextUnwatched?.id}
            />
          )}

          {/* Show info section */}
          <div className="bg-slate-800 rounded-lg p-6 mb-8">
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">{show.name}</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {show.genres.map(genre => (
                    <span
                      key={genre}
                      className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <p className="text-slate-300 mb-4">{stripHtml(show.summary)}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Status:</span>
                    <span
                      className={`ml-2 ${
                        show.status === 'Running' ? 'text-green-400' : 'text-slate-300'
                      }`}
                    >
                      {show.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400">Network:</span>
                    <span className="ml-2 text-slate-300">
                      {show.network?.name || show.webChannel?.name || 'N/A'}
                    </span>
                  </div>
                  {show.premiered && (
                    <div>
                      <span className="text-slate-400">Premiered:</span>
                      <span className="ml-2 text-slate-300">{show.premiered}</span>
                    </div>
                  )}
                  {show.rating.average && (
                    <div>
                      <span className="text-slate-400">Rating:</span>
                      <span className="ml-2 text-slate-300">{show.rating.average}/10</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Episodes list */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Episodes</h3>
            {episodes && markedEpisodes && (
              <EpisodeList
                episodes={episodes}
                markedEpisodes={markedEpisodes}
                nextUnwatchedId={nextUnwatched?.id}
                showId={show.id}
              />
            )}
          </div>
        </main>
      </div>
    </AuthGuard>
  )
}
